import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useServiceOnboarding } from 'contexts/service-onboarding'
import { useWhistleblowing } from 'contexts/whistleblowing'
import RoleProvider, { useRole } from 'contexts/role'
import { useMe } from 'contexts/me'
import { cls } from 'utilities/dom'
import { getShortName } from 'utilities/person'
import Modal, { useModal } from 'modals/dialog'
import Form from 'components/form/controller'
import PeopleField from 'components/form/field/people'
import { Helper } from 'components/form/field/s'
import Message from 'components/message'
import WordList from 'components/word-list'
import { Name as RoleName } from 'components/role'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'

const WhistleblowingSetUpCaseHandlers = ({ rolesAdmin, onDone }) => {
    const { formatMessage } = useIntl()
    const { dismiss } = useModal()

    const {
        configuration,

        activate,
        deactivate,
        fetch: refreshWhistleblowing
    } = useWhistleblowing()

    const {
        fetchRole,
        addGrant
    } = useRole()

    const { isItMyOwnId } = useMe()

    const {
        onboarder,
        updateOnboardingStatus
    } = useServiceOnboarding()

    const [handlers, setHandlers] = useState([])
    const [activating, setActivating] = useState(false)
    const [error, setError] = useState(null)

    const setUp = async ({ handlers }) => {
        const userIds = handlers.map(({ id }) => id)
        const grantedMyself = userIds.some(isItMyOwnId)

        setActivating(true)

        const { ok: activateOk } = await activate()

        let setUpOk = true
        if(rolesAdmin) {
            // The activate call spawns the role, so now that it exists,
            // we fetch it to get the UUID needed for adding the grant
            await fetchRole()

            const result = await addGrant({ userIds })
            setUpOk = result.ok
        }

        setActivating(false)

        if(activateOk && setUpOk) {
            refreshWhistleblowing()
            !!onboarder && updateOnboardingStatus({ setupWhistleblowing: 'completed' })

            return void onDone?.(grantedMyself)
        } else if(activateOk) {
            await deactivate()
        }

        setError(formatMessage({
            id: 'whistleblowing_set_up_error',
            defaultMessage: 'Something went wrong. Please try again.'
        }))
    }

    return (
        <>
            <Modal.Header
                heading={formatMessage({
                    id: 'whistleblowing_set_up_heading',
                    defaultMessage: 'Activate whistleblowing'
                })}
                caption={formatMessage({
                    id: 'whistleblowing_set_up_description',
                    defaultMessage: 'We will create a new system role with full access to handle Whistleblowing cases.'
                })} />
            <Form
                layout="vertical"
                onSubmit={setUp}>
                {({ trigger }) => (
                    <>
                        <Modal.Body>
                            <PeopleField
                                name="handlers"
                                label={formatMessage({
                                    id: 'incident_settings_case_handlers_heading',
                                    defaultMessage: 'Case handlers'
                                })}
                                field={{ required: true }}
                                {...(!!configuration?.handlers?.length ? {
                                    picker: {
                                        query: {
                                            filter: {
                                                excludeIds: configuration?.handlers?.map(({ id }) => id) ?? []
                                            }
                                        }
                                    }
                                } : null)}
                                enabled={rolesAdmin}
                                onChange={({ handlers }) => setHandlers(handlers)} />
                            <Helper>
                                <FormattedMessage
                                    id="incident_set_up_handlers_helper"
                                    defaultMessage="These are the people who will handle incoming reports." />
                            </Helper>
                            {(!!handlers.length && !error) && (
                                <Message
                                    type="info"
                                    message={formatMessage({
                                        id: 'role_organization_grant_summary',
                                        defaultMessage: 'The role {role} will be granted to {people} organization-wide.'
                                    }, {
                                        role: <strong><RoleName name="whistleblowing-handler" /></strong>,
                                        people: (
                                            <WordList
                                                words={handlers.map(getShortName)}
                                                wordRenderer={name => <strong>{name}</strong>} />
                                        )
                                    })} />
                            )}
                            {(!rolesAdmin && !error) && (
                                <Message
                                    type="warning"
                                    className="preserve-line-breaks"
                                    message={formatMessage({
                                        id: 'incident_set_up_no_roles_access',
                                        defaultMessage: 'To be able to add case handlers, you need access to manage roles and permissions.\n\nIncoming cases can only be handled once a Roles administrator assigns at least one case handler.'
                                    })} />
                            )}
                            {!!error && (
                                <Message
                                    type="error"
                                    message={error} />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Actions>
                                <Plain onClick={dismiss}>
                                    <FormattedMessage
                                        id="action_cancel"
                                        defaultMessage="Cancel" />
                                </Plain>
                                <ButtonSubmit
                                    className={cls([
                                        'constructive',
                                        activating && 'loading'
                                    ])}
                                    disabled={(rolesAdmin && !handlers.length) || activating}
                                    ref={trigger}>
                                    <FormattedMessage
                                        id="integration_action_activate"
                                        defaultMessage="Activate" />
                                </ButtonSubmit>
                            </Actions>
                        </Modal.Footer>
                    </>
                )}
            </Form>
        </>
    )
}

export default props => (
    <RoleProvider
        domain="organization"
        {...(props.rolesAdmin ? { id: 'whistleblowing-handler' } : null)}
        fetchOnMount={false}
        fetchNamedOnMount={false}>
        <WhistleblowingSetUpCaseHandlers {...props} />
    </RoleProvider>
)